//Gallery/GalleryPresentation.tsx

import React, { useEffect, useState, useRef } from 'react';
import Hls from "hls.js";
import type { Post, MediaEntry } from "./GalleryUIFunctions";

// ---------------- Types ----------------
interface Props {
  post: Post;
  startIndex: number;
  onClose: () => void;
}

////////////////////////////////////////////////////////    
//HLS PLAYER
////////////////////////////////////////////////////////    

export const HLSPlayer = ({
  src,
  poster,
  muted,
  active,
  style = {},
  onOrientation,
  isLandscape
}: {
  src: string;
  poster?: string;
  muted: boolean;
  active: boolean;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
  isLandscape?: boolean | null;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  //Play/pause
  const PlayIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <path d="M7 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
    <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
  </svg>
);

  //Loudspeakers
  const SpeakerOnIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15 9a4 4 0 0 1 0 6" />
    <path d="M18 7a7 7 0 0 1 0 10" />
  </svg>
);


const SpeakerOffIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

  /* ---------------- HLS attach ---------------- */
  useEffect(() => {
  const video = videoRef.current;
  if (!video || !src) return;

  let hls: Hls | null = null;
  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
    video.load();
  } else if (Hls.isSupported()) {
    hls = new Hls({ startLevel: 0, enableWorker: true });
    hls.loadSource(src);
    hls.attachMedia(video);
  }

  return () => {
    if (hls) hls.destroy();
  };
}, [src]);

  /* ---------------- autoplay logic ---------------- */
  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  if (!active) return;

  if (!userPaused) {
    v.play().catch(() => {});
  }
}, [active, userPaused]);

  /* ---------------- progress tracking ---------------- */
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (value: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = (v.duration * value) / 100;
  };

  /* ---------------- time ---------------- */
  const formatTime = (sec: number) => {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

  /* ---------------- volume ---------------- */
  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolume(value);
  };

  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  v.muted = isMuted;
}, [isMuted]);

  /* ---------------- play/pause ---------------- */
  const togglePlay = () => {
  const v = videoRef.current;
  if (!v) return;

  if (!v.paused && !v.ended) {
    v.pause();
    setUserPaused(true);
  } else {
    v.play().catch(() => {});
    setUserPaused(false);
  }
};
  
  /* ---------------- render ---------------- */

 return (
  <div
    style={{
      position: "relative",
      width: "auto",
      height: "auto",
      maxWidth: "100%",
      maxHeight: "100%",
      borderRadius: 12,
      overflow: "hidden",
      transform: isLandscape ? undefined : "translateY(-11px)", 

    }}
    onMouseEnter={() => setShowControls(true)}
    onMouseLeave={() => setShowControls(false)}
  >
    {/* VIDEO */}
    <video
      ref={videoRef}
      poster={poster}
      muted={isMuted}
      playsInline
      preload="auto"
      controls={false}
      onPointerDown={togglePlay}
      onLoadedMetadata={(e) => {
        emitOrientation(e.currentTarget);
      }}
      onLoadedData={(e) => emitOrientation(e.currentTarget)}
      onCanPlay={(e) => emitOrientation(e.currentTarget)}
      onTimeUpdate={onTimeUpdate}
      onPlay={() => {
  setIsPlaying(true);
  setUserPaused(false);
}}
onPause={() => {
  setIsPlaying(false);
}}
//For the replay this onEnded if I dont want replay remove
onEnded={() => {
  const v = videoRef.current;
  if (!v) return;

  v.currentTime = 0;
  v.play().catch(() => {});
}}
 style={{
  width: "auto",
  height: "100%",
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
  display: "block",
  transform: "translateZ(0)",
  backfaceVisibility: "hidden",
  ...style,
}}
    />

  {/* CONTROLS */}
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        opacity: showControls ? 1 : 0,
        transition: "opacity 200ms ease",
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: "hidden",
      }}
      onClick={(e) => e.stopPropagation()} 
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: isLandscape ? "7px 14px" : "5px 8px",
        }}
      >
        {/* LEFT — PLAY */}
        <div style={{ display: "flex", alignItems: "center" }}>
    <button
  onPointerDown={(e) => {
    e.stopPropagation();
    togglePlay();
  }}
  style={controlBtn}
>
  {isPlaying ? <PauseIcon /> : <PlayIcon />}
</button>
</div>

{/* CENTER — MAIN SCRUBBER */}
<div
  style={{
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    padding: "0 6px",
    maxWidth: isLandscape ? "82%" : "55%",   // 🔥 limits how wide the scrubber can ever become
    minWidth: 120,     // keeps it usable on small videos
  }}
>
  <input
    type="range"
    min={0}
    max={100}
    value={progress}
    onChange={(e) => handleScrub(Number(e.target.value))}
    className="custom-scrubber"
    style={{
      width: "100%",
      height: 3,
      background: `linear-gradient(
      to right,
      #d8cece 0%,
      #d8cece ${progress}%,
      #555 ${progress}%,
      #555 100%
    )`,
    }}
  />
</div>


{/* RIGHT — TIME + VOLUME */}
<div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, }} >
{/* TIME */}
<span
  style={{
    fontSize: 11,
    color: "#bbb",
    whiteSpace: "nowrap",
    marginLeft: -2,
    marginRight: 7,
    fontVariantNumeric: "tabular-nums", // 👈 stable width numbers
  }}
>
  {formatTime(videoRef.current?.currentTime || 0)}/
  {formatTime(videoRef.current?.duration || 0)}
</span>


{/* SPEAKER */}
<button
  onClick={(e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  }}
    style={controlBtnVolume}
>
{isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
</button>

{/* VOLUME */}
<input
  type="range"
  min={0}
  max={1}
  step={0.05}
  value={isMuted ? 0 : volume}
  onChange={(e) => {
    const val = Number(e.target.value);
        setIsMuted(val === 0);
        handleVolume(val);
        }}
  className="volume-slider"
  style={{
    width: isLandscape ? 85 : 65,
    height: 2,
    background: `linear-gradient(
      to right,
      #bcb3b3 0%,
      #d8cece ${(isMuted ? 0 : volume) * 100}%,
      #666 ${(isMuted ? 0 : volume) * 100}%,
      #666 100%
    )`,
    }}
          />
        </div>
      </div>
    </div>
  </div>
);

};

const controlBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 24,      
  lineHeight: 1,   
  padding: 0,      
};

const controlBtnVolume: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,
  lineHeight: 1,
  padding: 0,
};



// ---------------- Component ----------------


const GalleryPresentation: React.FC<Props> = ({
  post,
  startIndex,
  onClose,
}) => {

const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');

const mediaUrlOf = (m: MediaEntry): string => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.isHls && m.videoId) return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
  return m.url ?? '';
};

// Track which videos are landscape
const [isVideoLandscape, setIsVideoLandscape] = useState<Record<number, boolean>>({});
const [currentIndex, setCurrentIndex] = useState(startIndex);

const goPrev = () =>
  setCurrentIndex(i => Math.max(0, i - 1));

const goNext = () =>
  setCurrentIndex(i => Math.min(post.media.length - 1, i + 1));

// Update handlers
const handleImgLoad = (index: number, ev: React.SyntheticEvent<HTMLImageElement>) => {
  const img = ev.currentTarget;
  const w = img.naturalWidth || 0;
  const h = img.naturalHeight || 0;
  const landscape = w >= h;
  setIsLandscape(prev => (prev[index] === landscape ? prev : { ...prev, [index]: landscape }));
};

  // Track which grid images are landscape
const [isLandscape, setIsLandscape] = useState<Record<number, boolean>>({});

useEffect(() => {
  const prev = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  return () => {
    document.body.style.overflow = prev;
  };
}, []);

useEffect(() => {
  setCurrentIndex(startIndex);
}, [post._id, startIndex]);


useEffect(() => {
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') goPrev();
    if (e.key === 'ArrowRight') goNext();
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, [post.media.length]);

return (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100000,
      backgroundColor: '#000',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}
  >
    {/* ❌ CLOSE */}
    <button
      onClick={onClose}
      style={{
        position: 'absolute',
        top: 16,
        right: 20,
        zIndex: 100001,
        background: 'rgba(0,0,0,0.6)',
        color: '#666',
        border: 'none',
        fontSize: 16,
        cursor: 'pointer',
        padding: 8,
        borderRadius: 999,
      }}
    >
      ✖
    </button>

    {/* ← PREV */}
    {currentIndex > 0 && (
      <button
        onClick={goPrev}
        style={{
          position: 'absolute',
          left: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 28,
          background: 'none',
          color: '#666',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        ‹
      </button>
    )}

    {/* → NEXT */}
    {currentIndex < post.media.length - 1 && (
      <button
        onClick={goNext}
        style={{
          position: 'absolute',
          right: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 28,
          background: 'none',
          color: '#666',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        ›
      </button>
    )}

    {/* 🎬 ACTIVE MEDIA */}
    {(() => {
      const m = post.media[currentIndex];
      if (!m) return null;

      const isVideo = m.isHls === true;
      const url = mediaUrlOf(m);

      const musicLabel =
        [m.musicTitle, m.musicArtist].filter(Boolean).join(" — ");

      const isLandscapeNow =
        isLandscape[currentIndex] ??
        isVideoLandscape[currentIndex] ??
        false;

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* MEDIA BLOCK */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              width: isLandscapeNow ? "92vw" : "auto",
              height: isLandscapeNow ? "auto" : "calc(100vh - 28px)",
              maxHeight: isLandscapeNow ? "80vh" : "calc(100vh - 28px)",
            }}
          >
            {isVideo ? (
              <HLSPlayer
                src={url}
                muted
                active
                isLandscape={isLandscapeNow}
                onOrientation={(land) => {
                  setIsVideoLandscape(prev => ({
                  ...prev,
                 [currentIndex]: land
                 }));
                 }}
                style={{
                  width: "auto",
                  height: "auto",
                  maxWidth: isLandscapeNow ? "93vw" : "100vh",
                  maxHeight: isLandscapeNow ? "93vh" : "100vh",
                  objectFit: "contain",
                  borderRadius: 12,
                  }}
              />
            ) : (
              <img
                src={url}
                alt="presentation-media"
                onLoad={(e) => handleImgLoad(currentIndex, e)}
                style={{        
                  width: "auto",
                  height: "auto",
                  maxWidth: isLandscapeNow ? "93vw" : "97vh",
                  maxHeight: isLandscapeNow ? "93vh" : "97vh",
                  objectFit: "contain",
                  borderRadius: 12,
                 }}
              />
            )}
          </div>

{/* 🎵 MUSIC INFO UNDER MEDIA */}
{musicLabel && (
  <div
    style={{
      position: "fixed",
      bottom: 0,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: 12,
      color: "#777",
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "0px 5px 3px 5px",
      borderRadius: 999,
      background: "transparent",
      maxWidth: "50vw",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      zIndex: 100002,
    }}
  >
    <span>🎵</span>

    {m.musicId ? (
      <a
        href={`/musicdetails/${m.musicId}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          color: "#aaa",
          textDecoration: "none",
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {musicLabel}
      </a>
    ) : (
      <span>{musicLabel}</span>
    )}
  </div>
)}
 </div>
  );
 })()}
</div>
);
};

export default GalleryPresentation;
